import { js2xml } from "xml-js";
import * as filesaver from "file-saver";
import * as Cesium from "cesium";
import { XmlConstant } from "./xml-constants";
import { EarthBrainUtils } from "../utilities/earthbrainutility";
import { distanceUnits } from "../settings/viewer-settings";
/**
 * Usage
 * LandXML.export(triangulated Mesh Data)
 */
// biome-ignore lint/complexity/noStaticOnlyClass: public API is LandXML.*, kept for compatibility
export class LandXML {
    /**
     * Builds the Units element for a distance unit.
     * Area/volume units follow the distance unit; LandXML 1.2 has no survey-foot
     * variants of them, so ft and usfeet share squareFoot / cubicYard.
     * Attribute values follow the LandXML-1.2 enumerations (impLinear/impArea/
     * impVolume/impTemperature/impPressure and their met* counterparts).
     */
    static getUnitObject(distanceUnit) {
        let linearUnit;
        let areaUnit;
        let volumeUnit;
        let temperatureUnit;
        let pressureUnit;
        let unitElementName;
        switch (distanceUnit) {
            case distanceUnits.FEET:
            case distanceUnits.US_SURVEY_FEET:
                linearUnit =
                    distanceUnit === distanceUnits.FEET ? "foot" : "USSurveyFoot";
                areaUnit = "squareFoot";
                volumeUnit = "cubicYard";
                temperatureUnit = "fahrenheit";
                pressureUnit = "inchHG";
                unitElementName = "Imperial";
                break;
            default:
                linearUnit = "meter";
                areaUnit = "squareMeter";
                volumeUnit = "cubicMeter";
                temperatureUnit = "celsius";
                pressureUnit = "milliBars";
                unitElementName = "Metric";
                break;
        }
        return {
            type: "element",
            name: "Units",
            elements: [
                {
                    type: "element",
                    name: unitElementName,
                    attributes: {
                        areaUnit,
                        linearUnit,
                        volumeUnit,
                        temperatureUnit,
                        pressureUnit,
                        angularUnit: "decimal degrees",
                        directionUnit: "decimal degrees",
                    },
                },
            ],
        };
    }
    static getApplicationObject(AppName = "cct-EarthBrain", manufacturer = "EARTHBRAIN", manufacturerURL = "https://www.earthbrain.com/") {
        return {
            type: "element",
            name: "Project",
            attributes: {
                name: AppName,
                manufacturer,
                manufacturerURL,
            },
        };
    }
    static getPntsObj(positions) {
        const pnts = [];
        for (let i = 0; i < positions.length; i += 3) {
            const id = i + 1;
            pnts.push({
                type: "element",
                name: "P",
                attributes: {
                    id: Math.floor(id / 3 + 1),
                },
                elements: [
                    {
                        type: "text",
                        text: `${positions[i]} `,
                    },
                    {
                        type: "text",
                        text: `${positions[i + 1]} `,
                    },
                    {
                        type: "text",
                        text: `${positions[i + 2]}`,
                    },
                ],
            });
        }
        return {
            type: "element",
            name: "Pnts",
            elements: pnts,
        };
    }
    static getFacesObj(indices) {
        const faces = [];
        for (let i = 0; i < indices.length; i += 3) {
            faces.push({
                type: "element",
                name: "F",
                // "attributes": {
                //     id: Math.floor(i / 3) + 1,
                // },
                elements: [
                    {
                        type: "text",
                        text: `${indices[i] + 1} `,
                    },
                    {
                        type: "text",
                        text: `${indices[i + 1] + 1} `,
                    },
                    {
                        type: "text",
                        text: `${indices[i + 2] + 1}`,
                    },
                ],
            });
        }
        return {
            type: "element",
            name: "Faces",
            elements: faces,
        };
    }
    static getSurfacesObject(pnts, faces, SurfaceName = "surface", surfaceType = "TIN") {
        return {
            type: "element",
            name: "Surfaces",
            elements: [
                {
                    type: "element",
                    name: "Surface",
                    attributes: {
                        name: SurfaceName,
                    },
                    elements: [
                        {
                            type: "element",
                            name: "Definition",
                            attributes: {
                                surfType: surfaceType,
                            },
                            elements: [pnts, faces],
                        },
                    ],
                },
            ],
        };
    }
    static getLandXmlObj(surfaceObject, projectName, distanceUnit) {
        const date = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return {
            type: "element",
            name: "LandXML",
            attributes: {
                "xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
                "xsi:schemaLocation": "http://www.landxml.org/schema/LandXML-1.2 http://www.landxml.org/schema/LandXML-1.2/LandXML-1.2.xsd",
                date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
                time: `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`,
                version: "1.2",
                language: "Japanese",
                readOnly: "false",
                xmlns: "http://www.landxml.org/schema/LandXML-1.2",
            },
            elements: [
                this.getUnitObject(distanceUnit),
                this.getApplicationObject(projectName),
                surfaceObject,
            ],
        };
    }
    static prepareJSObj(landxml) {
        return {
            declaration: {
                attributes: {
                    version: XmlConstant.version,
                    encoding: XmlConstant.encoding,
                },
            },
            elements: [landxml],
        };
    }
    static export(polylineVolumeGeometry, fileName = "test", projectName, distanceUnit) {
        try {
            const normalizedUnit = distanceUnit ?? distanceUnits.METERS;
            const geometry = Cesium.PolylineVolumeGeometry.createGeometry(polylineVolumeGeometry);
            const pnts = this.getPntsObj(geometry.attributes.position.values);
            const faces = this.getFacesObj(geometry.indices);
            const surfaces = this.getSurfacesObject(pnts, faces);
            const landxml = this.getLandXmlObj(surfaces, projectName, normalizedUnit);
            const xmlobj = this.prepareJSObj(landxml);
            const xml = js2xml(xmlobj, { spaces: 1 });
            const blob = new Blob([xml], { type: "text/plain;charset=utf-8" });
            filesaver.saveAs(blob, `${fileName}.xml`);
        }
        catch (e) {
            console.error("Error while exporting the landxml", e);
        }
        //debug
        //this.exportObj(geometry.attributes.position.values as Float64Array, geometry.indices as unknown as Uint16Array);
    }
    /**
     * Exports the mesh as a file or an xml string
     * If filename is passed downlaods the file. Returns the xml string if not passed.
     * @param vertices vertices
     * @param indices
     * @param projectName
     * @param fileName [Optional] if passed downloads the file with passed name
     * @param distanceUnit [Optional] distance unit for the Units element; vertices
     * must already be converted into that unit by the caller
     * @returns
     */
    static exportMesh(vertices, indices, projectName, fileName, objectName, distanceUnit) {
        let res = undefined;
        try {
            const normalizedUnit = distanceUnit ?? distanceUnits.METERS;
            const uniqueGeometry = EarthBrainUtils.removeDuplicateVertices({
                vertices,
                indices,
            });
            EarthBrainUtils.makeFaceVerticesOrderAntiClockwise(uniqueGeometry, new Cesium.Cartesian3());
            const pnts = this.getPntsObj(new Float64Array(uniqueGeometry.vertices));
            const faces = this.getFacesObj(new Uint16Array(uniqueGeometry.indices));
            const surfaces = this.getSurfacesObject(pnts, faces);
            if (objectName) {
                surfaces.elements[0].attributes.name = objectName;
            }
            const landxml = this.getLandXmlObj(surfaces, projectName, normalizedUnit);
            const xmlobj = this.prepareJSObj(landxml);
            const xml = js2xml(xmlobj, { spaces: 1 });
            if (fileName) {
                const blob = new Blob([xml], { type: "text/plain;charset=utf-8" });
                filesaver.saveAs(blob, `${fileName}.xml`);
            }
            else {
                res = xml;
            }
        }
        catch (e) {
            console.error("Error while exporting the landxml", e);
        }
        //debug
        //this.exportObj(geometry.attributes.position.values as Float64Array, geometry.indices as unknown as Uint16Array);
        return res;
    }
    static exportObj(positions, indexes) {
        let stringstream = "";
        for (let i = 0; i < positions.length; i += 3) {
            stringstream += `v ${positions[i] / 100000} ${positions[i + 1] / 100000} ${positions[i + 2] / 100000}\n`;
        }
        for (let i = 0; i < indexes.length; i += 3) {
            stringstream += `f ${indexes[i]} ${indexes[i + 1]} ${indexes[i + 2]}\n`;
        }
        const blob = new Blob([stringstream], { type: "text/plain;charset=utf-8" });
        filesaver.saveAs(blob, "debugTest.obj");
    }
    static exportFlatGroundArea(polygonGeometry, fileName = "FlatGroundLandxml", projectName, distanceUnit) {
        try {
            const normalizedUnit = distanceUnit ?? distanceUnits.METERS;
            const geometry = Cesium.PolygonGeometry.createGeometry(polygonGeometry);
            const pnts = this.getPntsObj(geometry.attributes.position.values);
            const faces = this.getFacesObj(geometry.indices);
            const surfaces = this.getSurfacesObject(pnts, faces);
            const landxml = this.getLandXmlObj(surfaces, projectName, normalizedUnit);
            const xmlobj = this.prepareJSObj(landxml);
            const xml = js2xml(xmlobj, { spaces: 1 });
            const blob = new Blob([xml], { type: "text/plain;charset=utf-8" });
            filesaver.saveAs(blob, `${fileName}.xml`);
        }
        catch (e) {
            console.error("Error while exporting the landxml", e);
        }
    }
}
//# sourceMappingURL=xml-export.js.map